import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { Filters } from '#src/lib/performance.tsx';
import { FilterKey } from '#src/lib/performance.tsx';

import { ScoresBreakdown } from './ScoresBreakdown/index.tsx';
import { ScoresCard } from './ScoresCard/index.tsx';
import { Stats } from './Stats/index.tsx';
import type { PerformanceBlocks_anyPlayer } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  width: 100%;
`;

const Row = styled(Vertical)`
  width: 100%;
  @media ${desktopAndAbove} {
    flex-direction: row;
    align-items: flex-start;
    & > * {
      width: calc(50% - var(--intermediate-unit) / 2);
      flex-shrink: 0;
    }
  }
`;

const ScoresCardSkeleton = styled(Skeleton)`
  width: 100%;
  height: 84px;
  border-radius: var(--double-unit);
`;

const ScoresBreakdownSkeleton = styled(Skeleton)`
  width: 100%;
  height: 252px;
  border-radius: var(--double-unit);
`;

const StatsSkeleton = styled(Skeleton)`
  width: 100%;
  height: 456px;
  border-radius: var(--double-unit);
`;

type Props = {
  player: Nullable<PerformanceBlocks_anyPlayer>;
  filters: Filters;
  loading?: boolean;
};
export const PerformanceBlocks = ({ player, filters, loading }: Props) => {
  const position = filters.position || player?.cardPositions[0];

  return (
    <Root gap={1.5}>
      {loading ? (
        <ScoresCardSkeleton />
      ) : (
        <ScoresCard
          sport={player?.sport}
          averageLastScore={player?.anySo5AverageLastScore}
          mode={filters[FilterKey.MODE]}
        />
      )}

      <Row gap={1.5}>
        {loading ? (
          <ScoresBreakdownSkeleton />
        ) : (
          <ScoresBreakdown averageLastScore={player?.anySo5AverageLastScore} />
        )}
        {loading ? (
          <StatsSkeleton />
        ) : (
          <Stats
            averageLastScore={player?.anySo5AverageLastScore}
            position={position}
            mode={filters[FilterKey.MODE]}
          />
        )}
      </Row>
    </Root>
  );
};

PerformanceBlocks.fragments = {
  anyPlayer: gql`
    fragment PerformanceBlocks_anyPlayer on AnyPlayerInterface {
      slug
      sport
      cardPositions
      anySo5AverageLastScore(
        competitionSlug: $competitionSlug
        location: $location
        position: $position
        span: $span
        teamSlug: $teamSlug
      ) {
        id
        ...ScoresCard_averageLastScore
        ...ScoresBreakdown_averageLastScore
        ...Stats_averageLastScore
      }
    }
    ${ScoresCard.fragments.averageLastScore}
    ${ScoresBreakdown.fragments.averageLastScore}
    ${Stats.fragments.averageLastScore}
  ` as TypedDocumentNode<PerformanceBlocks_anyPlayer>,
};
